import { Component, Input, forwardRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService } from '../../../services/file-upload.service';
import { I18nService } from '../../../services/i18n.service';

export interface AvatarUploadField {
  type: 'avatar-upload';
  label: string;
  id: string;
  name: string;
  required?: boolean;
  maxFileSize?: number; // MB
  acceptedFormats?: string[];
  displayMode?: 'circle' | 'square' | 'rectangle';
  placeholder?: string;
}

export interface AvatarUploadValue {
  type: 'avatar-upload';
  value: {
    fileName: string;
    fileUrl: string;
    fileSize: number;
    uploadedAt: string;
    displayMode: string;
    blobName?: string;
    previewUrl?: string;
  };
}

@Component({
  selector: 'app-form-avatar-upload',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './form-avatar-upload.component.html',
  styleUrls: ['./form-avatar-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAvatarUploadComponent),
      multi: true
    }
  ]
})
export class FormAvatarUploadComponent implements ControlValueAccessor {
  @Input() label: string = '大頭照';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() maxFileSize: number = 5; // MB
  @Input() acceptedFormats: string[] = ['.jpg', '.jpeg', '.png', '.gif'];
  @Input() displayMode: 'circle' | 'square' | 'rectangle' = 'square';
  @Input() placeholder: string = '點擊上傳照片';

  @Output() avatarChange = new EventEmitter<AvatarUploadValue['value'] | null>();

  avatar: AvatarUploadValue['value'] | null = null;
  isUploading = false;
  uploadProgress = 0;
  isDragOver = false;

  // Camera functionality
  isCameraActive = false;
  videoStream: MediaStream | null = null;
  capturedImage: string | null = null;
  showCameraPreview = false;
  

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private fileUploadService: FileUploadService,
    private i18nService: I18nService
  ) {}

  // ControlValueAccessor implementation
  writeValue(value: AvatarUploadValue['value'] | null): void {
    this.avatar = value;
    if (this.avatar && this.avatar.blobName && !this.avatar.previewUrl) {
      this.generatePreviewUrl();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handle disabled state if needed
  }

  // File selection handler
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileUpload(input.files[0]);
    }
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  // File upload handler
  handleFileUpload(file: File): void {
    if (!this.validateFile(file)) {
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    this.fileUploadService.uploadFile(file).subscribe({
      next: (response) => {
        if (response.type === 'progress') {
          this.uploadProgress = response.progress;
        } else if (response.type === 'success' && response.response) {
          const blobName = response.response.data.blobName;
          const avatarData: AvatarUploadValue['value'] = {
            fileName: file.name,
            fileUrl: blobName,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            displayMode: this.displayMode,
            blobName: blobName
          };

          this.avatar = avatarData;
          this.generatePreviewUrl();
          this.avatarChange.emit(this.avatar);
          this.onChange(this.avatar);
          this.onTouched();
          this.isUploading = false;
        }
      },
      error: (error) => {
        console.error('Error uploading avatar:', error);
        alert('上傳失敗，請重試');
        this.isUploading = false;
      }
    });
  }

  // File validation
  private validateFile(file: File): boolean {
    // Check file size
    const maxSizeBytes = this.maxFileSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      alert(this.i18nService.translate('file_upload.file_size_exceeded', { 
        maxSize: this.maxFileSize 
      }));
      return false;
    }

    // Check file format
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.acceptedFormats.map(format => format.toLowerCase()).includes(fileExtension)) {
      alert(`只允許上傳 ${this.acceptedFormats.join(', ')} 格式的檔案`);
      return false;
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('只允許上傳圖片檔案');
      return false;
    }

    return true;
  }

  // Generate preview URL
  private generatePreviewUrl(): void {
    if (this.avatar && this.avatar.blobName) {
      this.fileUploadService.generateViewUrl(this.avatar.blobName).subscribe({
        next: (response) => {

          console.log('Preview URL response:', response);
          // Check if response contains viewUrl

          if (response && response.data.viewUrl) {
            this.avatar!.previewUrl = response.data.viewUrl;
          }
        },
        error: (error) => {
          console.error('Error generating preview URL:', error);
        }
      });
    }
  }

  // Remove avatar
  removeAvatar(): void {
    this.avatar = null;
    this.avatarChange.emit(null);
    this.onChange(null);
    this.onTouched();

    // Reset file input
    const fileInput = document.getElementById(this.id) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Trigger file selection
  triggerFileSelection(): void {
    const fileInput = document.getElementById(this.id) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get display mode class
  getDisplayModeClass(): string {
    return `avatar-display-${this.displayMode}`;
  }

  // Get recommended dimensions text
  getRecommendedDimensions(): string {
    switch (this.displayMode) {
      case 'circle':
        return '建議尺寸：正方形圖片（如 300x300）';
      case 'square':
        return '建議尺寸：正方形圖片（如 300x300）';
      case 'rectangle':
        return '建議尺寸：長方形圖片（如 400x300）';
      default:
        return '';
    }
  }

  // Camera functionality methods
  async startCamera(): Promise<void> {
    try {
      this.videoStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user' // Front camera preference
        } 
      });
      this.isCameraActive = true;
      this.showCameraPreview = true;
      this.capturedImage = null;
      
      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        const videoElement = document.getElementById(`camera-${this.id}`) as HTMLVideoElement;
        if (videoElement && this.videoStream) {
          videoElement.srcObject = this.videoStream;
          videoElement.play();
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('無法存取攝影機，請確認瀏覽器權限');
    }
  }

  capturePhoto(): void {
    const videoElement = document.getElementById(`camera-${this.id}`) as HTMLVideoElement;
    const canvasElement = document.getElementById(`canvas-${this.id}`) as HTMLCanvasElement;
    
    if (videoElement && canvasElement) {
      const context = canvasElement.getContext('2d');
      if (context) {
        // Set canvas size to match video
        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        
        // Draw video frame to canvas
        context.drawImage(videoElement, 0, 0);
        
        // Get image data as base64
        this.capturedImage = canvasElement.toDataURL('image/jpeg', 0.8);
        this.showCameraPreview = false;
      }
    }
  }

  retakePhoto(): void {
    this.capturedImage = null;
    this.showCameraPreview = true;
  }

  confirmPhoto(): void {
    if (this.capturedImage) {
      // Convert base64 to blob
      this.base64ToBlob(this.capturedImage).then(blob => {
        // Create file from blob
        const fileName = `camera-photo-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        
        // Upload the captured photo
        this.handleFileUpload(file);
        this.stopCamera();
      });
    }
  }

  stopCamera(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }
    this.isCameraActive = false;
    this.showCameraPreview = false;
    this.capturedImage = null;
  }

  // Helper method to convert base64 to blob
  private async base64ToBlob(base64: string): Promise<Blob> {
    const response = await fetch(base64);
    return response.blob();
  }

  // Check if camera is supported
  isCameraSupported(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

}
